import { z } from "zod";
import { Id, IsoDate, Locale, Page, PageQuery } from "./common.ts";
import { AiLabels, Channel, Role } from "./domain.ts";
import { OrgPlanSummary } from "./entitlements.ts";

/**
 * Platform admin console (plan 13): Malfak staff only. Every `/v1/admin/*` route requires the
 * `platform_admin` access level (403 otherwise) and every unsafe call is written to `audit_log`.
 * Path params are never named `orgId`: that name means "the caller's org" to the API and the web client.
 * POSTs here require an `Idempotency-Key` header.
 */
type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
export interface AdminRouteDef<P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny> {
  method: Method;
  path: string;
  params?: P;
  query?: Q;
  body?: B;
  response: R;
  status?: number;
  summary: string;
}
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: AdminRouteDef<P, Q, B, R>) => d;
const Ok = z.object({ ok: z.literal(true) });
const Empty = z.object({});
const byId = z.object({ id: z.string().uuid() });
const flag = z.enum(["true", "false"]).transform((v) => v === "true");

// ---------------------------------------------------------------- links (traceability)
/** Deep links into the tools that hold the details. null when that tool isn't configured. */
export const TraceLinks = z.object({ temporal: z.string().nullable(), langfuse: z.string().nullable() });

// ---------------------------------------------------------------- users & orgs
export const PlatformRole = z.enum(["platform_admin"]);
export const AdminUser = z.object({
  id: Id,
  email: z.string().email(),
  name: z.string(),
  /** ALP-85: from the approved access request (null for accounts staff created). */
  username: z.string().nullable().optional(),
  locale: Locale,
  platformRole: PlatformRole.nullable(),
  /** "env" = listed in PLATFORM_ADMIN_EMAILS (can't be revoked from the console), "db" = granted here. */
  platformRoleSource: z.enum(["env", "db"]).nullable(),
  status: z.enum(["active", "disabled"]),
  disabledAt: IsoDate.nullable(),
  createdAt: IsoDate,
  lastLoginAt: IsoDate.nullable(),
  activeSessions: z.number(),
  orgs: z.array(z.object({ id: Id, name: z.string(), role: Role })),
});
export const AdminUserQuery = PageQuery.extend({
  q: z.string().max(200).optional(),
  status: z.enum(["active", "disabled"]).optional(),
  platformAdmin: flag.optional(),
});
/** A new organization created from the admin console (owner = the user being created, or an existing / new user). */
export const NewOrgInput = z.object({
  name: z.string().trim().min(1).max(120),
  /** ALP-81 plan; default trial (trial end = now + the trial plan's days). */
  planKey: z.enum(["trial", "free", "pro", "business"]).default("trial"),
});
/**
 * Every user belongs to an organization (a login with no membership got a broken app): pick an existing org + role
 * (`membership`) OR create a new organization the user owns (`newOrg`). Exactly one is required.
 */
export const CreateUserRequest = z
  .object({
    email: z.string().email(),
    name: z.string().min(1).max(120),
    locale: Locale.default("ar"),
    membership: z.object({ orgId: Id, role: Role }).optional(),
    newOrg: NewOrgInput.optional(),
    platformAdmin: z.boolean().default(false),
  })
  .refine((b) => !!b.membership !== !!b.newOrg, { message: "choose an organization and role, or create a new organization", path: ["membership"] });
/** Admin → Orgs → Create organization: the owner is an existing user or a new one (temporary password shown once). */
export const CreateOrgRequest = NewOrgInput.extend({
  owner: z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("existing"), userId: Id }),
    z.object({ mode: z.literal("new"), email: z.string().email(), name: z.string().min(1).max(120), locale: Locale.default("ar") }),
  ]),
});
/** The temporary password is shown once; replays of the same Idempotency-Key return null. */
export const UserWithTempPassword = z.object({ user: AdminUser, tempPassword: z.string().nullable() });
export const UpdateUserRequest = z.object({
  name: z.string().min(1).max(120).optional(),
  disabled: z.boolean().optional(),
  platformAdmin: z.boolean().optional(),
});

/** ALP-81: plan keys (plans are data; see entitlements.ts). */
export const Plan = z.enum(["trial", "free", "pro", "business"]);
export const AdminOrg = z.object({
  id: Id,
  name: z.string(),
  slug: z.string(),
  plan: Plan,
  createdAt: IsoDate,
  members: z.number(),
  accounts: z.number(),
  owner: z.object({ id: Id, email: z.string(), name: z.string() }).nullable(),
  /** Org-level kill-switch overrides that are on (e.g. ["automationPaused"]). */
  overrides: z.array(z.string()),
  /** ALP-81: plan, trial and the headline usage bars (present on list + detail). */
  entitlements: OrgPlanSummary.optional(),
});
export const AdminOrgQuery = PageQuery.extend({ q: z.string().max(200).optional(), plan: Plan.optional() });
export const AdminOrgDetail = AdminOrg.extend({
  memberList: z.array(z.object({ user: z.object({ id: Id, email: z.string(), name: z.string(), status: z.enum(["active", "disabled"]) }), role: Role, joinedAt: IsoDate })),
  accountList: z.array(z.object({ id: Id, channel: Channel, username: z.string(), status: z.string(), aiEnabled: z.boolean(), connectedAt: IsoDate })),
  usage: z.object({ comments30d: z.number(), runs30d: z.number(), failedRuns30d: z.number() }),
});
export const UpdateOrgRequest = z.object({ name: z.string().min(1).max(120).optional(), plan: Plan.optional() });
export const SetMemberRequest = z.object({ role: Role });

// ---------------------------------------------------------------- AI models (LiteLLM)
export const ModelDeployment = z.object({
  /** LiteLLM `model_info.id`. */
  id: z.string(),
  /** The alias the app calls (`model_name`), e.g. malfak-fast. */
  alias: z.string(),
  /** The model behind it (`litellm_params.model`, provider name scrubbed), e.g. openai/gpt-5-mini. Send it back to switch. */
  model: z.string(),
  /** Human name the UI shows (e.g. "GPT-5 mini"); never a raw provider id (ALP-80 owner rule). */
  displayName: z.string().optional(),
  /** Neutral gateway label ("Primary gateway" or AI_PROVIDER_LABEL); the upstream provider is never named. */
  provider: z.string(),
  /** Always null: provider hosts are never exposed (kept for compatibility). */
  apiBase: z.string().nullable(),
  mode: z.string().nullable(),
  tags: z.array(z.string()),
  /** "config" = config.yaml (read-only), "db" = added at runtime (editable). */
  source: z.enum(["config", "db"]),
  inputCostPerMTok: z.number().nullable(),
  outputCostPerMTok: z.number().nullable(),
});
export const AiAlias = z.object({
  alias: z.string(),
  /** Every deployment behind the alias (LiteLLM load-balances between them). */
  deployments: z.array(ModelDeployment),
  fallbacks: z.array(z.string()),
  managed: z.boolean(), // starts with malfak-
});
export const AiGateway = z.object({
  url: z.string(),
  reachable: z.boolean(),
  /** Runtime edits need LiteLLM with STORE_MODEL_IN_DB=True and a database. */
  manageable: z.boolean(),
  /** Master key, masked (e.g. "sk-…a1b2"), or null when not configured. */
  keyHint: z.string().nullable(),
  error: z.string().nullable(),
});
export const AiModelsOverview = z.object({ gateway: AiGateway, aliases: z.array(AiAlias), models: z.array(ModelDeployment) });
export const CreateModelRequest = z.object({
  alias: z.string().regex(/^[a-z0-9][a-z0-9._-]{1,62}$/),
  model: z.string().min(3).max(200),
  /** Reuse a credential the gateway already holds (e.g. "os.environ/CORE42_API_KEY"); raw keys are never accepted. */
  credentialRef: z.string().regex(/^os\.environ\/[A-Z0-9_]+$/).optional(),
  apiBaseRef: z.string().regex(/^os\.environ\/[A-Z0-9_]+$/).optional(),
  mode: z.enum(["chat", "embedding", "audio_transcription", "audio_speech", "image_generation"]).optional(),
  tags: z.array(z.string().regex(/^[a-z0-9:_.-]{1,40}$/)).max(20).default([]),
});
export const UpdateModelRequest = z.object({
  model: z.string().min(3).max(200).optional(),
  tags: z.array(z.string().regex(/^[a-z0-9:_.-]{1,40}$/)).max(20).optional(),
});
export const SwitchAliasRequest = z.object({ model: z.string().min(3).max(200), reason: z.string().max(500).optional() });
export const SwitchAliasResult = z.object({ alias: AiAlias, before: z.array(z.string()), after: z.array(z.string()), warnings: z.array(z.string()) });
export const AiSpend = z.object({
  available: z.boolean(),
  reason: z.string().nullable(),
  currency: z.literal("USD"),
  total: z.number(),
  byModel: z.array(z.object({ key: z.string(), spend: z.number(), requests: z.number().nullable() })),
  byDay: z.array(z.object({ day: z.string(), spend: z.number() })),
});

// ---------------------------------------------------------------- flows: webhook inbox
export const WebhookStatus = z.enum(["received", "processed", "failed"]);
export const WebhookEventSummary = z.object({
  id: Id,
  source: z.string(),
  /** Meta `object` (instagram, page, whatsapp_business_account …). */
  object: z.string().nullable(),
  fields: z.array(z.string()),
  dedupKey: z.string(),
  status: WebhookStatus,
  signedBy: z.string().nullable(),
  receivedAt: IsoDate,
  processedAt: IsoDate.nullable(),
  error: z.string().nullable(),
  /** HandleInboundEvent workflow id (= dedup key; replays add `:replay:<n>`). */
  workflowId: z.string(),
  replays: z.number(),
  links: TraceLinks,
});
export const WebhookQuery = PageQuery.extend({
  status: WebhookStatus.optional(),
  source: z.string().max(40).optional(),
  object: z.string().max(60).optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  q: z.string().max(200).optional(),
});
export const AdminRun = z.object({
  id: Id,
  orgId: Id,
  orgName: z.string().nullable(),
  ruleId: Id.nullable(),
  ruleName: z.string().nullable(),
  eventRef: z.string(),
  matched: z.boolean(),
  labels: AiLabels.nullable(),
  action: z.string().nullable(),
  result: z.enum(["executed", "pending_approval", "skipped", "failed", "suggested"]),
  reason: z.string().nullable(),
  requestId: z.string().nullable(),
  workflowId: z.string().nullable(),
  createdAt: IsoDate,
  links: TraceLinks,
});
export const WebhookEventDetail = WebhookEventSummary.extend({
  payload: z.unknown(),
  runs: z.array(AdminRun),
  history: z.array(z.object({ at: IsoDate, action: z.string(), actor: z.string().nullable(), detail: z.string().nullable() })),
});
export const ReplayRequest = z.object({ reason: z.string().max(500).optional() });
export const ReplayResult = z.object({ workflowId: z.string(), started: z.boolean(), error: z.string().nullable(), event: WebhookEventSummary });

// ---------------------------------------------------------------- flows: Temporal workflows
export const WorkflowStatus = z.enum(["RUNNING", "COMPLETED", "FAILED", "CANCELLED", "TERMINATED", "CONTINUED_AS_NEW", "TIMED_OUT", "UNKNOWN"]);
export const WorkflowSummary = z.object({
  workflowId: z.string(),
  runId: z.string(),
  type: z.string(),
  taskQueue: z.string(),
  status: WorkflowStatus,
  startTime: IsoDate,
  closeTime: IsoDate.nullable(),
  historyLength: z.number().nullable(),
  memo: z.record(z.string(), z.string()),
  links: TraceLinks,
});
export const WorkflowQuery = z.object({
  taskQueue: z.string().max(60).optional(),
  status: WorkflowStatus.optional(),
  type: z.string().max(80).optional(),
  q: z.string().max(200).optional(), // workflow id prefix
  pageToken: z.string().max(2000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const WorkflowList = z.object({
  available: z.boolean(),
  error: z.string().nullable(),
  items: z.array(WorkflowSummary),
  nextPageToken: z.string().nullable(),
});
export const WorkflowHistoryEvent = z.object({ id: z.number(), type: z.string(), time: IsoDate, detail: z.string().nullable() });
export const WorkflowDetail = WorkflowSummary.extend({
  input: z.unknown(),
  result: z.unknown(),
  failure: z.string().nullable(),
  pendingActivities: z.array(z.object({ activityType: z.string(), attempt: z.number(), state: z.string(), lastFailure: z.string().nullable() })),
  history: z.array(WorkflowHistoryEvent),
  historyTruncated: z.boolean(),
});
export const WorkflowRef = z.object({ workflowId: z.string().min(1).max(400) });
export const WorkflowActionRequest = z.object({ reason: z.string().min(3).max(500) });
export const WorkflowActionResult = z.object({ ok: z.boolean(), workflowId: z.string(), runId: z.string().nullable(), error: z.string().nullable() });

// ---------------------------------------------------------------- audit & errors
export const AuditEntry = z.object({
  id: Id,
  at: IsoDate,
  orgId: Id.nullable(),
  orgName: z.string().nullable(),
  actorType: z.string(),
  actorId: z.string().nullable(),
  actorEmail: z.string().nullable(),
  action: z.string(),
  target: z.string().nullable(),
  before: z.unknown(),
  after: z.unknown(),
  requestId: z.string().nullable(),
});
export const AuditQuery = PageQuery.extend({
  actorId: z.string().max(80).optional(),
  orgId: Id.optional(), // query (not path), so the API doesn't treat it as the caller's org
  action: z.string().max(80).optional(), // prefix match
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  q: z.string().max(200).optional(),
});
export const ErrorEvent = z.object({
  kind: z.enum(["webhook", "operation", "external_action", "automation_run"]),
  id: Id,
  orgId: Id.nullable(),
  message: z.string(),
  ref: z.string().nullable(),
  at: IsoDate,
  links: TraceLinks,
});

// ---------------------------------------------------------------- settings & kill switches
export const KillSwitches = z.object({
  /** Rules stop running; incoming events are still stored and runs are recorded as skipped. */
  automationPaused: z.boolean(),
  /** No LLM calls: AI features answer 503, automation degrades to rules + human approval. */
  aiDisabled: z.boolean(),
  /** Dry-run: nothing is sent to channels (replies, DMs, hides, publishing). */
  outboundDryRun: z.boolean(),
});
export const PlatformSettings = KillSwitches.extend({ maintenanceBanner: z.string().max(500).nullable() });
export const PlatformSettingsPatch = PlatformSettings.partial();
/** Per-org overrides: null clears an override (the global value applies). */
export const OrgSettingsPatch = z.object({
  automationPaused: z.boolean().nullable().optional(),
  aiDisabled: z.boolean().nullable().optional(),
  outboundDryRun: z.boolean().nullable().optional(),
  maintenanceBanner: z.string().max(500).nullable().optional(),
});
export const SettingsMeta = z.object({ updatedAt: IsoDate.nullable(), updatedBy: z.string().nullable() });
export const PlatformSettingsView = z.object({
  global: PlatformSettings,
  meta: SettingsMeta,
  orgOverrides: z.array(z.object({ orgId: Id, orgName: z.string(), overrides: OrgSettingsPatch, effective: PlatformSettings })),
});
/** What every signed-in user may know: the banner and whether the switches affect them. */
export const PlatformNotice = z.object({ maintenanceBanner: z.string().nullable(), switches: KillSwitches });

// ---------------------------------------------------------------- health & overview
export const DependencyCheck = z.object({ ok: z.boolean(), latencyMs: z.number(), error: z.string().optional(), url: z.string().nullable().optional(), version: z.string().nullable().optional(), required: z.boolean() });
export const QueueDepth = z.object({ taskQueue: z.string(), backlog: z.number().nullable(), pollers: z.number().nullable(), running: z.number().nullable() });
/**
 * Semantic index (Dify Knowledge) across orgs (QA F1/F17: every document was in indexing `error` while `dify=true`).
 * Counts come from each org dataset's last recorded check (worker reconcile, re-index, org knowledge status).
 */
export const SemanticIndexHealth = z.object({
  state: z.enum(["ok", "degraded", "unavailable"]),
  /** DIFY_DATASET_API_KEY set (else the lexical fallback is all there is). */
  configured: z.boolean(),
  reachable: z.boolean(),
  orgs: z.number().int(),
  orgsWithErrors: z.number().int(),
  indexed: z.number().int(),
  indexing: z.number().int(),
  error: z.number().int(),
  lastError: z.string().nullable(),
  /** Oldest recorded check among the datasets (null = never checked). */
  checkedAt: IsoDate.nullable(),
  /** Orgs with the most failed documents (re-index one of them, or all). */
  worst: z.array(z.object({ orgId: Id, orgName: z.string(), error: z.number().int(), indexed: z.number().int(), lastError: z.string().nullable() })).max(10),
});
export const SemanticResubmitRequest = z.object({ orgId: Id.optional() });
export const SemanticResubmitResult = z.object({ started: z.boolean(), workflowId: z.string(), orgId: Id.nullable() });
export const SystemHealth = z.object({
  status: z.enum(["ok", "degraded", "down"]),
  semantic: SemanticIndexHealth.optional(),
  checkedAt: IsoDate,
  checks: z.record(z.string(), DependencyCheck),
  queues: z.object({
    webhookInbox: z.object({ unprocessed: z.number(), failed24h: z.number(), oldestUnprocessedAgeSec: z.number().nullable() }),
    operations: z.object({ queued: z.number(), running: z.number() }),
    approvalsPending: z.number(),
    temporal: z.array(QueueDepth),
  }),
  versions: z.object({ api: z.string(), node: z.string(), env: z.string(), schema: z.string().nullable() }),
});
export const AdminOverview = z.object({
  kpis: z.object({
    users: z.number(),
    disabledUsers: z.number(),
    orgs: z.number(),
    accounts: z.number(),
    accountsNeedingReauth: z.number(),
    webhooks24h: z.number(),
    runs24h: z.number(),
    failedRuns24h: z.number(),
    externalActions24h: z.number(),
  }),
  activity: z.array(z.object({ hour: IsoDate, webhooks: z.number(), runs: z.number(), failures: z.number() })),
  semantic: SemanticIndexHealth.optional(),
  switches: PlatformSettings,
  links: z.object({ temporalUi: z.string().nullable(), langfuse: z.string().nullable(), litellm: z.string().nullable() }),
});

// ---------------------------------------------------------------- calendar (ALP-73): platform-level review of global events
export const AdminCalendarReviewRequest = z.object({ decision: z.enum(["publish", "reject"]), reason: z.string().trim().max(500).optional() });
export const AdminCalendarReviewResult = z.object({
  id: Id,
  titleEn: z.string(),
  status: z.enum(["published", "rejected"]),
  previousStatus: z.string(),
  reviewedAt: IsoDate,
});

/** ALP-85: the console's Calendar review tab lists global events across every scope (default: the ones needing review). */
export const AdminCalendarEvent = z.object({
  id: Id,
  scope: z.string(),
  type: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  title: z.object({ en: z.string(), ar: z.string() }),
  mood: z.string(),
  severity: z.string().nullable(),
  source: z.string(),
  sourceUrl: z.string().nullable(),
  confidence: z.number(),
  status: z.enum(["published", "needs_review", "rejected", "removed"]),
  reviewedAt: IsoDate.nullable(),
  fetchedAt: IsoDate,
});
export const AdminCalendarEventsQuery = z.object({
  status: z.enum(["needs_review", "published", "rejected", "all"]).default("needs_review"),
  type: z.string().max(40).optional(),
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export const adminRoutes = {
  platformNotice: route({ method: "GET", path: "/v1/platform/notice", response: PlatformNotice, summary: "Maintenance banner + kill switches in effect for the caller's org (any signed-in user)" }),

  adminOverview: route({ method: "GET", path: "/v1/admin/overview", response: AdminOverview, summary: "Platform KPIs, 24h activity, switches, tool links" }),
  adminHealth: route({ method: "GET", path: "/v1/admin/health", response: SystemHealth, summary: "Every dependency, queue depths, versions" }),
  adminSemanticResubmit: route({
    method: "POST", path: "/v1/admin/semantic/resubmit", body: SemanticResubmitRequest, response: SemanticResubmitResult, status: 202,
    summary: "Re-index: re-submit semantic-index documents in error and enqueue missing ones (one org, or every org)",
  }),

  adminUsers: route({ method: "GET", path: "/v1/admin/users", query: AdminUserQuery, response: Page(AdminUser), summary: "Search users" }),
  adminUser: route({ method: "GET", path: "/v1/admin/users/:id", params: byId, response: AdminUser, summary: "User detail" }),
  adminCreateUser: route({ method: "POST", path: "/v1/admin/users", body: CreateUserRequest, response: UserWithTempPassword, status: 201, summary: "Create a user with a one-time temporary password" }),
  adminUpdateUser: route({ method: "PATCH", path: "/v1/admin/users/:id", params: byId, body: UpdateUserRequest, response: AdminUser, summary: "Rename, disable/enable (revokes sessions), grant/revoke platform admin" }),
  adminResetPassword: route({ method: "POST", path: "/v1/admin/users/:id/reset-password", params: byId, body: Empty, response: z.object({ tempPassword: z.string().nullable() }), summary: "New one-time temporary password; revokes the user's sessions" }),

  adminOrgs: route({ method: "GET", path: "/v1/admin/orgs", query: AdminOrgQuery, response: Page(AdminOrg), summary: "Search organizations" }),
  adminCreateOrg: route({ method: "POST", path: "/v1/admin/orgs", body: CreateOrgRequest, response: z.object({ org: AdminOrgDetail, owner: z.object({ userId: Id, email: z.string(), tempPassword: z.string().nullable() }) }), status: 201, summary: "Create an organization with an owner (existing or new user); plan via entitlements (default Trial); audited, idempotent" }),
  adminOrg: route({ method: "GET", path: "/v1/admin/orgs/:id", params: byId, response: AdminOrgDetail, summary: "Organization detail (members, accounts, usage)" }),
  adminUpdateOrg: route({ method: "PATCH", path: "/v1/admin/orgs/:id", params: byId, body: UpdateOrgRequest, response: AdminOrgDetail, summary: "Rename / change plan" }),
  adminSetMember: route({ method: "PUT", path: "/v1/admin/orgs/:id/members/:userId", params: byId.extend({ userId: z.string().uuid() }), body: SetMemberRequest, response: AdminOrgDetail, summary: "Add a member or change their role (an org keeps at least one owner)" }),
  adminRemoveMember: route({ method: "DELETE", path: "/v1/admin/orgs/:id/members/:userId", params: byId.extend({ userId: z.string().uuid() }), response: AdminOrgDetail, summary: "Remove a member (not the last owner)" }),
  adminOrgSettings: route({ method: "PUT", path: "/v1/admin/orgs/:id/settings", params: byId, body: OrgSettingsPatch, response: PlatformSettingsView, summary: "Per-org kill-switch overrides" }),

  adminAiModels: route({ method: "GET", path: "/v1/admin/ai/models", response: AiModelsOverview, summary: "LiteLLM aliases, deployments, tags (master key never leaves the API)" }),
  adminCreateModel: route({ method: "POST", path: "/v1/admin/ai/models", body: CreateModelRequest, response: ModelDeployment, status: 201, summary: "Add a deployment behind an alias" }),
  adminUpdateModel: route({ method: "PATCH", path: "/v1/admin/ai/models/:modelId", params: z.object({ modelId: z.string().min(1).max(200) }), body: UpdateModelRequest, response: ModelDeployment, summary: "Change a deployment's model or tags" }),
  adminDeleteModel: route({ method: "DELETE", path: "/v1/admin/ai/models/:modelId", params: z.object({ modelId: z.string().min(1).max(200) }), response: Ok, summary: "Remove a runtime deployment" }),
  adminSwitchAlias: route({ method: "POST", path: "/v1/admin/ai/aliases/:alias/switch", params: z.object({ alias: z.string().min(1).max(64) }), body: SwitchAliasRequest, response: SwitchAliasResult, summary: "Point an alias at another provider model, at runtime" }),
  adminAiSpend: route({ method: "GET", path: "/v1/admin/ai/spend", query: z.object({ days: z.coerce.number().int().min(1).max(90).default(7) }), response: AiSpend, summary: "LLM spend (when the gateway records it)" }),

  adminWebhooks: route({ method: "GET", path: "/v1/admin/flows/webhooks", query: WebhookQuery, response: Page(WebhookEventSummary), summary: "Webhook inbox" }),
  adminWebhook: route({ method: "GET", path: "/v1/admin/flows/webhooks/:id", params: byId, response: WebhookEventDetail, summary: "Webhook event with payload, runs and replay history" }),
  adminReplayWebhook: route({ method: "POST", path: "/v1/admin/flows/webhooks/:id/replay", params: byId, body: ReplayRequest, response: ReplayResult, status: 202, summary: "Re-run HandleInboundEvent for a stored event (side effects stay idempotent)" }),
  adminWorkflows: route({ method: "GET", path: "/v1/admin/flows/workflows", query: WorkflowQuery, response: WorkflowList, summary: "Temporal workflows (visibility)" }),
  adminWorkflow: route({ method: "GET", path: "/v1/admin/flows/workflows/:workflowId", params: WorkflowRef, response: WorkflowDetail, summary: "Describe + history summary" }),
  adminCancelWorkflow: route({ method: "POST", path: "/v1/admin/flows/workflows/:workflowId/cancel", params: WorkflowRef, body: WorkflowActionRequest, response: WorkflowActionResult, summary: "Request cancellation" }),
  adminTerminateWorkflow: route({ method: "POST", path: "/v1/admin/flows/workflows/:workflowId/terminate", params: WorkflowRef, body: WorkflowActionRequest, response: WorkflowActionResult, summary: "Terminate immediately" }),
  adminRetryWorkflow: route({ method: "POST", path: "/v1/admin/flows/workflows/:workflowId/retry", params: WorkflowRef, body: WorkflowActionRequest, response: WorkflowActionResult, summary: "Reset to the first workflow task: re-runs with the same input and id" }),
  adminRuns: route({ method: "GET", path: "/v1/admin/flows/runs", query: PageQuery.extend({ org: Id.optional(), result: AdminRun.shape.result.optional(), q: z.string().max(200).optional() }), response: Page(AdminRun), summary: "Automation runs across orgs" }),

  adminAudit: route({ method: "GET", path: "/v1/admin/audit", query: AuditQuery, response: Page(AuditEntry), summary: "Audit log" }),
  adminErrors: route({ method: "GET", path: "/v1/admin/errors", query: z.object({ limit: z.coerce.number().int().min(1).max(200).default(50) }), response: z.array(ErrorEvent), summary: "Recent persisted failures (webhooks, operations, external actions, runs)" }),

  adminSettings: route({ method: "GET", path: "/v1/admin/settings", response: PlatformSettingsView, summary: "Global settings, kill switches and per-org overrides" }),
  adminUpdateSettings: route({ method: "PUT", path: "/v1/admin/settings", body: PlatformSettingsPatch, response: PlatformSettingsView, summary: "Change global settings / kill switches" }),

  adminCalendarEvents: route({ method: "GET", path: "/v1/admin/calendar/events", query: AdminCalendarEventsQuery, response: z.object({ items: z.array(AdminCalendarEvent), counts: z.object({ needsReview: z.number() }) }), summary: "Global calendar events for platform review (default: needs_review, crises first, newest first)" }),
  adminReviewCalendarEvent: route({ method: "POST", path: "/v1/admin/calendar/events/:id/review", params: byId, body: AdminCalendarReviewRequest, response: AdminCalendarReviewResult, summary: "Publish or reject a global calendar event for every org (sticks across refreshes)" }),
} as const;

export type AdminUser = z.infer<typeof AdminUser>;
export type AdminOrg = z.infer<typeof AdminOrg>;
export type AdminOrgDetail = z.infer<typeof AdminOrgDetail>;
export type ModelDeployment = z.infer<typeof ModelDeployment>;
export type AiAlias = z.infer<typeof AiAlias>;
export type AiModelsOverview = z.infer<typeof AiModelsOverview>;
export type AiSpend = z.infer<typeof AiSpend>;
export type WebhookEventSummary = z.infer<typeof WebhookEventSummary>;
export type WebhookEventDetail = z.infer<typeof WebhookEventDetail>;
export type AdminRun = z.infer<typeof AdminRun>;
export type WorkflowSummary = z.infer<typeof WorkflowSummary>;
export type WorkflowDetail = z.infer<typeof WorkflowDetail>;
export type WorkflowStatus = z.infer<typeof WorkflowStatus>;
export type AuditEntry = z.infer<typeof AuditEntry>;
export type ErrorEvent = z.infer<typeof ErrorEvent>;
export type KillSwitches = z.infer<typeof KillSwitches>;
export type PlatformSettings = z.infer<typeof PlatformSettings>;
export type PlatformSettingsView = z.infer<typeof PlatformSettingsView>;
export type OrgSettingsPatch = z.infer<typeof OrgSettingsPatch>;
export type SystemHealth = z.infer<typeof SystemHealth>;
export type SemanticIndexHealth = z.infer<typeof SemanticIndexHealth>;
export type AdminOverview = z.infer<typeof AdminOverview>;
export type TraceLinks = z.infer<typeof TraceLinks>;
export type AdminCalendarEvent = z.infer<typeof AdminCalendarEvent>;
